import classNames from "classnames";
import ContentShell from "components/ContentShell";
import Loader from "components/Loader";
import { notify } from "components/Notifications";
import PixiContainer from "components/PixiContainer";
import { AppEventTypes, CanvasEvents, CanvasMessage, loaderTypes } from "core/enums";
import {
  OddsDisplayEnum,
  useAllBadgesLazyQuery,
  useOddsLazyQuery,
  useUpdateUserExtraDataMutation,
  useUpdateUserSettingsMutation,
} from "generated/graphql";
import { initI18n } from "i18n/i18nHelper";
import { translationKeys } from "i18n/translationKeys.enums";
import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import sessionHelper from "service/sessionHelper";
import useGameStore from "store/gameStore";
import styles from "styles/pages/home/home.module.css";
import getConfig from "util/configHelper";
import appEventInstance from "util/eventHandler";
import { timer } from "util/miscUtils";
import { shallow } from "zustand/shallow";

const Home = () => {
  const { configData } = getConfig();
  const [updateUserSettingsMutation] = useUpdateUserSettingsMutation();
  const [
    updateUserExtraDataMutation,
    { data: userExtraData, loading: isUpdateUserExtraDataLoading, error: isUpdateUserExtraDataError },
  ] = useUpdateUserExtraDataMutation();

  const {
    updateDrawerStates,
    setMyTeamResponse,
    setIframeRef,
    setSelectedOddsDisplay,
    setSettingsData,
    setShowLoader,
    setIsShowTeamList,
    isOpenSettingsDrawer,
    setIsInitDataAvailable,
    setIsUserCheating,
    isUserCheating,
    setAllBadgesList,
    isOpenLeaderBoardDrawer,
    isOpenLeagueDrawer,
    setHideReactOverLay,
    hideReactOverLay,
    isOpenStakePannelOptions,
    settingsData,
    updateModalStates,
    selectedOddsDisplay,
  } = useGameStore(
    (state) => ({
      updateDrawerStates: state.updateDrawerStates,
      selectedOddsDisplay: state.selectedOddsDisplay,
      settingsData: state.settingsData,
      setShowLoader: state.setShowLoader,
      updateModalStates: state.updateModalStates,
      setMyTeamResponse: state.setMyTeamResponse,
      isOpenSettingsDrawer: state.drawerStates.isOpenSettingsDrawer,
      setIframeRef: state.setIframeRef,
      setSelectedOddsDisplay: state.setSelectedOddsDisplay,
      setSettingsData: state.setSettingsData,
      myTeamResponse: state.myTeamResponse,
      hideReactPage: state.hideReactPage,
      setHideReactPage: state.setHideReactPage,
      setIsInitDataAvailable: state.setIsInitDataAvailable,
      isInitDataAvailable: state.isInitDataAvailable,
      setIsShowTeamList: state.setIsShowTeamList,
      setAllBadgesList: state.setAllBadgesList,
      isUserCheating: state.isUserCheating,
      hideReactOverLay: state.hideReactOverLay,
      setHideReactOverLay: state.setHideReactOverLay,
      setIsUserCheating: state.setIsUserCheating,
      isOpenLeaderBoardDrawer: state.drawerStates.isOpenLeaderBoardDrawer,
      isOpenLeagueDrawer: state.drawerStates.isOpenLeagueDrawer,
      isOpenVaultXpModal: state.modalStates.isOpenVaultXpModal,
      isOpenStakePannelOptions: state.drawerStates.isOpenStakePannelOptions,
    }),
    shallow,
  );
  const Bets = React.lazy(() => import("pages/bets/Bets"));
  const Wallet = React.lazy(() => import("pages/wallet/Wallet"));

  const teamDataRef = React.useRef({});
  const paddingRef = React.useRef({ top: 74, bottom: 79 });
  const [dimension, setDimension] = React.useState({ top: 74, bottom: 79 });
  const [page, setPage] = React.useState(CanvasEvents.home);
  const iframe = React.useRef<HTMLIFrameElement | null>(null);
  const [fetchStakeOptionsOddsList] = useOddsLazyQuery();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const isUnderMaintenaceRef = React.useRef(false);

  const isDebugBuild = queryParams.get("isDebugBuild") === "true";

  const _transferInfoToPixi = async (data: { type: string; value: any }) => {
    const iframeRef = useGameStore.getState().iframeRef;
    //@ts-ignore
    if (
      iframeRef &&
      //@ts-ignore
      iframeRef?.contentWindow
    ) {
      //@ts-ignore
      iframeRef?.contentWindow?.gameExtension(data);
    }
  };

  const [fetchAllBadges] = useAllBadgesLazyQuery({
    onCompleted: (data) => {
      setAllBadgesList(data?.teamManager?.allBadges);
    },
  });

  const _getCurrStateValues = () => {
    let myTeamResponse = useGameStore.getState().myTeamResponse;
    let allBadgesResponse = useGameStore.getState().allBadgesList;

    return { myTeamResponse, allBadgesResponse };
  };
  const updateSettings = async (updatedSettings: { oddsDisplay: OddsDisplayEnum; audio: boolean }) => {
    try {
      const resp = await updateUserSettingsMutation({
        variables: { input: updatedSettings },
      });
    } catch (error) {
      appEventInstance.dispatch({
        eventType: AppEventTypes.toggleErrorModal,
        payload: { error, success: false },
      });
    }
  };

  const _handleCanvasEvent = async (info: CustomEvent) => {
    const { detail } = info;
    // console.log({ detail, settingsData });
    if (detail?.padding?.top) {
      appEventInstance.dispatch({ eventType: AppEventTypes.toggleDrawerHeight, payload: { ...detail?.padding } });
      if (paddingRef.current.top !== detail?.padding?.top || paddingRef.current.bottom !== detail?.padding?.bottom) {
        setDimension({ ...detail?.padding });
        paddingRef.current = { ...detail?.padding };
      }
    }
    try {
      switch (detail.messageName) {
        case CanvasEvents.initData:
          initI18n(detail?.data?.language || "en"); // fall back to en
          await timer(100);
          sessionHelper?.onSignIn({
            ...sessionHelper,
            ...detail.data,
          });
          if (detail?.data?.isUnderMaintenance) {
            isUnderMaintenaceRef.current = true; // is this to not render any of the react pages ...
            return;
          }
          setIsInitDataAvailable(true);
          if (isDebugBuild && detail?.data?.useCheat) {
            setIsUserCheating(true);
            sessionHelper.onSignIn({ ...sessionHelper, useCheat: true });
          }

          //@ts-ignore
          await _transferInfoToPixi?.({
            type: CanvasMessage.configData,
            value: { ...configData },
          });
          fetchAllBadges();

          break;
        case CanvasEvents.reloadApp:
          window.location.reload();
          break;
        case CanvasEvents.meData:
          let settingsData = detail?.data?.settings;
          setSelectedOddsDisplay(settingsData?.oddsDisplay);
          setSettingsData(settingsData);
          sessionHelper.onSignIn({
            ...sessionHelper,
            extraData: {
              ...sessionHelper.extraData,
              isStartMatchFlowShow: detail?.data?.extraData?.isStartMatchFlowShow
                ? detail?.data?.extraData?.isStartMatchFlowShow
                : false, //
            },
          });
          break;
        case CanvasEvents.teamData:
          teamDataRef.current = { ...detail?.data };
          setMyTeamResponse(detail?.data);
          break;
        case CanvasEvents.udpateBalance:
          sessionHelper.onSignIn({
            ...sessionHelper,
            balance: detail.balance,
            pbfCoin: detail.pbfCoin,
          });
          break;
        case CanvasEvents.teamEdit:
          const { myTeamResponse, allBadgesResponse } = _getCurrStateValues();
          teamDataRef.current = { ...myTeamResponse };
          appEventInstance.dispatch({
            eventType: AppEventTypes.populateTeamEdit,
            payload: { data: { ...teamDataRef.current }, badgeList: allBadgesResponse },
          });
          //@ts-ignore
          await _transferInfoToPixi?.({
            type: CanvasMessage.stopCanvasRendering,
            value: {},
          });
          updateDrawerStates({ isOpenTeamEditDrawer: true });
          break;
        case CanvasEvents.showPopUp:
          let canvasMessage = "";
          if (detail?.data?.id === "VOID_BET") {
            canvasMessage = translationKeys.VOID_BET;
          }
          appEventInstance.dispatch({
            eventType: AppEventTypes.toggleCanvasErrorModal,
            payload: { translationKey: canvasMessage, popupId: detail?.data?.id },
          });
          break;
        case CanvasEvents.showDisconnection:
          updateModalStates({ isOpenVaultXpModal: false });
          appEventInstance.dispatch({
            eventType: AppEventTypes.toggleCanvasErrorModal,
            payload: {
              translationKey: detail?.data?.message,
              // buttonText: detail?.data?.buttonText,
              title: detail?.data?.title,
              hideButton: true,
            },
          });
          break;
        case CanvasEvents.hideDisconnection:
          appEventInstance.dispatch({
            eventType: AppEventTypes.toggleCanvasErrorModal,
            payload: { translationKey: detail?.data?.message, closePopUp: true, buttonText: detail?.data?.buttonText },
          });
          break;
        case CanvasEvents.showSetting:
          if (!isOpenSettingsDrawer) {
            if (!isOpenStakePannelOptions) {
              //@ts-ignore
              await _transferInfoToPixi?.({
                type: CanvasMessage.stopCanvasRendering,
                value: {},
              });
            }
            updateDrawerStates({ isOpenSettingsDrawer: true });
            setTimeout(() => {
              updateDrawerStates({ isOpenLeaderBoardDrawer: false });
              updateDrawerStates({ isOpenLeagueDrawer: false });
            }, 500);
          }
          break;
        case CanvasEvents.showLeaderBoard:
          if (!isOpenLeaderBoardDrawer) {
            //@ts-ignore
            await _transferInfoToPixi?.({
              type: CanvasMessage.stopCanvasRendering,
              value: {},
            });
            updateDrawerStates({ isOpenLeaderBoardDrawer: true });
          }
          break;
        case CanvasEvents.showLeaderBoard_2_0:
          if (!isOpenLeagueDrawer) {
            //@ts-ignore
            await _transferInfoToPixi?.({
              type: CanvasMessage.stopCanvasRendering,
              value: {},
            });
            updateDrawerStates({ isOpenLeagueDrawer: true });
          }
          break;
        case CanvasEvents.showSnackBar:
          if (detail?.data?.message) {
            notify(detail.data);
          }
          break;
        case CanvasEvents.showBet:
          if (isUnderMaintenaceRef.current) {
            // for safe side if canvas fails to handle this helps hiding the react pages
            return;
          }
          //@ts-ignore
          await _transferInfoToPixi?.({
            type: CanvasMessage.stopCanvasRendering,
            value: {},
          });
          sessionHelper.onSignIn({
            ...sessionHelper,
            isFTU : detail?.data?.isFTUE,
          })

          setShowLoader({ isTrue: true, loaderFor: loaderTypes.oddsList });
          updateDrawerStates({ isOpenStakePannelOptions: true });
          try {
            const _oddsResp = await fetchStakeOptionsOddsList();
            if (_oddsResp?.data?.players?.odds?.matchId) {
              // unCommented Below
              sessionHelper.updateOddDetails({
                ..._oddsResp?.data?.players?.odds,
                teamA: detail?.data?.myTeam,
                teamB: detail?.data?.oppTeam,
              });
              if (sessionHelper.oddsDetails?.matchId.length) {
                setShowLoader({ isTrue: false, loaderFor: loaderTypes.oddsList });
                if (!sessionHelper?.showEos && detail?.data?.isFTUE && !sessionHelper.extraData?.isStartMatchFlowShow) {
                  await timer(700);
                  updateModalStates({ isOpenPlaceBetOrStartMatchModal: true });
                }
              }
            } else {
              appEventInstance.dispatch({
                eventType: AppEventTypes.toggleErrorModal,
                payload: { translationKey: translationKeys.Stake04 },
              });
            }
          } catch (error) {
            appEventInstance.dispatch({
              eventType: AppEventTypes.toggleErrorModal,
              payload: { error, success: false },
            });
          }
          break;
        case CanvasEvents.toggleSound:
          let isCheckInAudio = !settingsData?.audio;
          setSettingsData({ audio: isCheckInAudio });
          //@ts-ignore
          await _transferInfoToPixi?.({
            type: CanvasMessage.isAudioOn,
            value: { isAudioOn: isCheckInAudio },
          });
          await updateSettings({ oddsDisplay: selectedOddsDisplay as OddsDisplayEnum, audio: isCheckInAudio });

          break;
        case CanvasEvents.hideTeamList:
          setIsShowTeamList(false);
          break;
        case CanvasEvents.hideReactOverLay:
          setHideReactOverLay(true);
          break;
        case CanvasEvents.showReactOverLay:
          setHideReactOverLay(false);
          break;
        case CanvasEvents.home:
          setPage(detail.messageName);
          break;
        case CanvasEvents.teams:
          setPage(detail.messageName);
          break;
        case CanvasEvents.myBets:
          setShowLoader({ isTrue: true, loaderFor: loaderTypes.betHistory });
          setPage(detail.messageName);
          break;
        case CanvasEvents.hideReactPage:
          setPage(detail.messageName);
          break;
        case CanvasEvents.ftuiComplete:
          // console.log("is ftue Complete message ...")
          sessionHelper.onSignIn({ ...sessionHelper, isFTU: false });
          break;
        case CanvasEvents.wallet:
          setPage(detail?.messageName);
          break;
        case CanvasEvents.currentLeagueData:
          let isEnabled =
            sessionHelper?.startMatchBtnRestriction &&
            (sessionHelper?.startMatchBtnDiv ? sessionHelper?.startMatchBtnDiv : 0) >= detail?.data?.currentLeague;
          sessionHelper.onSignIn({
            ...sessionHelper,
            isCheckStartMatchFunctionalityEnabled: isEnabled,
          });
          break;
        default:
      }
    } catch (e) {
      console.log("Home: err", e);
    }
  };

  useEffect(() => {
    appEventInstance.listen(AppEventTypes.canvasEvent, _handleCanvasEvent);

    return () => {
      appEventInstance.remove(AppEventTypes.canvasEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (iframe.current) {
      setIframeRef(iframe.current);
    }
  }, [setIframeRef]);

  return (
    <ContentShell>
      <section className={styles.root}>
        <PixiContainer iframe={iframe} />
        {/* remove the below div forceBuildData once use Cheat functionality is checked by the client .... */}
        <div
          className={classNames(styles.forceBuildData, {
            [styles.hide]: !isUserCheating,
          })}
          style={{
            position: "absolute",
            top: "65px",
            left: 0,
            zIndex: 9999,
            width: "100%",
            textAlign: "center",
            WebkitTextStroke: "0.1px black",
          }}
        >
          <h1 style={{ color: "red", fontSize: "0.85rem", fontWeight: "bolder" }}>WARNING: FORCES ENABLED</h1>
        </div>
        {[CanvasEvents.home, CanvasEvents.teams, CanvasEvents.hideReactPage].includes(page) ? null : (
          <div
            id="pageContainer"
            style={{ top: `${dimension?.top || 0}px`, bottom: `${dimension?.bottom || 0}px` }}
            className={classNames(styles.default, {
              [styles.hideEl]: true,
              [styles.noWidth]: hideReactOverLay,
            })}
          >
            <Suspense
              fallback={
                <section className="h-full">
                  <Loader />
                </section>
              }
            >
              {/* <ForceSuspense shouldSuspend={false}> */}
              {page === CanvasEvents.myBets && <Bets />}
              {page === CanvasEvents.wallet && <Wallet />}
              {/* </ForceSuspense> */}
            </Suspense>
          </div>
        )}
      </section>
    </ContentShell>
  );
};

const ForceSuspense = ({ shouldSuspend, children }: any) => {
  if (shouldSuspend) {
    // This makes React suspend and show fallback
    throw new Promise((resolve) => setTimeout(resolve, 1000));
  }
  return children;
};

export default Home;
